import React from 'react';
import { clsx } from 'clsx';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/solid';

export type StatCardVariant = 'default' | 'gradient' | 'glass' | 'bordered' | 'primary' | 'secondary' | 'success' | 'warning' | 'error';
export type TrendDirection = 'up' | 'down' | 'neutral';

export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  iconBackground?: string;
  trend?: {
    value: string | number;
    direction: TrendDirection;
    label?: string;
  };
  variant?: StatCardVariant;
  href?: string;
  linkText?: string;
  loading?: boolean;
}

const variantStyles: Record<StatCardVariant, string> = {
  default: 'bg-white border border-neutral-200 shadow-soft',
  gradient: 'bg-gradient-to-br from-white to-neutral-50 border border-neutral-200 shadow-soft',
  glass: 'bg-white/80 backdrop-blur-lg border border-white/20 shadow-soft',
  bordered: 'bg-white border-2 border-neutral-200',
  primary: 'bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 shadow-soft',
  secondary: 'bg-gradient-to-br from-neutral-50 to-neutral-100 border border-neutral-200 shadow-soft',
  success: 'bg-gradient-to-br from-success-50 to-success-100 border border-success-200 shadow-soft',
  warning: 'bg-gradient-to-br from-warning-50 to-warning-100 border border-warning-200 shadow-soft',
  error: 'bg-gradient-to-br from-error-50 to-error-100 border border-error-200 shadow-soft',
};

export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  (
    {
      title,
      value,
      subtitle,
      icon,
      iconBackground = 'bg-primary-100',
      trend,
      variant = 'default',
      href,
      linkText = 'View all',
      loading = false,
      className,
      ...props
    },
    ref
  ) => {
    const trendColors = {
      up: 'text-success-600 bg-success-50',
      down: 'text-error-600 bg-error-50',
      neutral: 'text-neutral-600 bg-neutral-50',
    };

    if (loading) {
      return (
        <div
          ref={ref}
          className={clsx(
            'rounded-xl p-6',
            variantStyles[variant],
            className
          )}
          {...props}
        >
          <div className="animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-neutral-200" />
              <div className="flex-1">
                <div className="h-4 w-20 bg-neutral-200 rounded mb-2" />
                <div className="h-8 w-24 bg-neutral-200 rounded" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={clsx(
          'rounded-xl p-6 transition-all duration-300',
          'hover:shadow-card-hover hover:-translate-y-1',
          variantStyles[variant],
          className
        )}
        {...props}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {icon && (
              <div
                className={clsx(
                  'flex-shrink-0 flex items-center justify-center',
                  // Only apply default sizing/bg if no specific class overrides are playing a role, 
                  // but for backward compat we keep it simple: 
                  // If it's an image (detected by check?) No, let's just use a prop or check variant.
                  // Simpler: Just allow the container to be sized by input if needed, but for now let's just remove the strict inner span size constraint if the icon handles it.
                  'w-14 h-14 rounded-2xl',
                  iconBackground
                )}
              >
                {/* Remove fixed size on child to allow 3D icons to fill container */}
                <div className="flex items-center justify-center w-full h-full text-primary-600">
                  {icon}
                </div>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-neutral-500">{title}</p>
              <p className="text-2xl font-bold text-neutral-900 mt-1">{value}</p>
              {subtitle && (
                <p className="text-sm text-neutral-500 mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Trend indicator */}
          {trend && (
            <div
              className={clsx(
                'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                trendColors[trend.direction]
              )}
            >
              {trend.direction === 'up' && <ArrowTrendingUpIcon className="w-3 h-3" />}
              {trend.direction === 'down' && <ArrowTrendingDownIcon className="w-3 h-3" />}
              <span>{trend.value}</span>
            </div>
          )}
        </div>

        {/* Footer link */}
        {href && (
          <div className="mt-4 pt-4 border-t border-neutral-100">
            <a
              href={href}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              {linkText} →
            </a>
          </div>
        )}
      </div>
    );
  }
);

StatCard.displayName = 'StatCard';

export default StatCard;

